import React from "react";
import { useViewContent } from "@/lib/pixel";
import { Landmark, Mic2, Newspaper } from "lucide-react";
import { PageHero, Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton } from "@/components/primitives";
import LeadDialog, { DOCS } from "@/components/forms/LeadDialog";
import { SPONSOR_TIERS } from "@/data/site";
import { cn } from "@/lib/utils";

const WHY = [
  { icon: Landmark, t: "Policy-level visibility", d: "Your brand where the decisions are made." },
  { icon: Mic2, t: "The stage & the buyers", d: "Speaking platforms and direct access to decision-makers." },
  { icon: Newspaper, t: "Press & industry coverage", d: "The media reach a forum of this scale attracts." },
];

export default function Sponsor() {
  useViewContent("Sponsor Info");
  return (
    <>
      <PageHero
        overline="Sponsorship"
        title="Put your name on India's energy transition."
        subtitle="In front of the producers, buyers, investors and policymakers who decide where this market goes."
        image="/assets/site/sponsor-branding-visibility.jpg"
        imageFallback="https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
      />

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <Overline className="mb-4">Why sponsor</Overline>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Visibility where the decisions are made.</h2>
                <p className="mt-6 text-lg leading-relaxed text-navy/70">
                  Sponsorship places your brand at the centre of India's ethanol economy — with leadership visibility at policy level, speaking platforms, branding across both the conference and expo, direct access to decision-makers, and the media coverage a forum of this scale attracts. For technology providers and project developers, it's the clearest way to position ahead of competitors while billions in new capacity is still being planned.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Stagger className="grid gap-4">
                {WHY.map((w) => (
                  <StaggerItem key={w.t}>
                    <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-6 shadow-sm">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep"><w.icon className="h-5 w-5" /></span>
                      <div>
                        <h3 className="font-display text-lg font-bold text-navy">{w.t}</h3>
                        <p className="mt-1 text-sm text-navy/60">{w.d}</p>
                      </div>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Partnership options" title="Choose your level." />
          <Stagger className="mt-14 grid gap-6 lg:grid-cols-2">
            {SPONSOR_TIERS.map((t) => (
              <StaggerItem key={t.tier}>
                <div className={cn(
                  "relative flex h-full flex-col rounded-3xl border p-8 transition-all hover:-translate-y-1",
                  t.highlight ? "border-ethanolOrange bg-navy text-white shadow-2xl" : "border-border bg-parchment shadow-sm hover:shadow-xl"
                )}>
                  {t.highlight && <span className="absolute -top-3 left-8 rounded-full bg-ethanolOrange px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">Featured</span>}
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className={cn("font-display text-2xl font-bold", t.highlight ? "text-white" : "text-navy")}>{t.tier}</h3>
                    <span className={cn("text-sm font-semibold", t.highlight ? "text-emerald-300" : "text-emeraldDeep")}>{t.invest}</span>
                  </div>
                  <p className={cn("mt-2 text-sm font-medium uppercase tracking-wide", t.highlight ? "text-slate-400" : "text-navy/50")}>Best for · {t.best}</p>
                  <p className={cn("mt-4 flex-1", t.highlight ? "text-slate-300" : "text-navy/70")}>{t.includes}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-10 flex flex-wrap gap-4">
            <LeadDialog
              formType="sponsorship_proposal"
              title="Get the Sponsorship Proposal"
              description="Tiers, deliverables and branding opportunities — instant PDF download."
              ctaLabel="Get the proposal"
              successMsg="Sent. Your download is starting now."
              downloadUrl={DOCS.sponsorProposal.url}
              downloadName={DOCS.sponsorProposal.name}
              extraFields={[{ key: "tier", placeholder: "Tier of interest (optional)" }]}
              trigger={<CTAButton data-testid="download-proposal">Download the Sponsorship Proposal</CTAButton>}
            />
            <CTAButton to="/contact" variant="outline" className="text-navy">Talk to the team</CTAButton>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0A1B33] grain py-20 text-center">
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Sponsorship slots are limited by tier.</h2>
            <p className="mt-4 text-slate-300">Title and Platinum are single/limited. Secure your level early.</p>
            <div className="mt-8 flex justify-center">
              <LeadDialog
                formType="sponsor"
                title="Become a Sponsor"
                ctaLabel="Submit enquiry"
                successMsg="Thanks — our partnerships team will reach out."
                trigger={<CTAButton data-testid="become-sponsor">Become a Sponsor</CTAButton>}
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
